import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { cn } from "../lib/utils";
import { loginErrorMessage } from "../../utils/loginErrorMessage";

export function CopilotLoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Defaults to true: field sales reps expect PWA persistence; forgetting to
  // check the box caused repeated forced re-logins (refresh token landed in
  // sessionStorage and was wiped on PWA close).
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // If already authenticated, redirect to record page
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/copilot/record", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(email, password, stayLoggedIn);
      navigate("/copilot/record", { replace: true });
    } catch (err) {
      // Same status-to-message mapping as the main and portal sign-in forms:
      // 401 is bad credentials, 403 the api's inactive-account detail, 5xx the
      // api's detail or an outage line, no response a timeout or network error.
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center overflow-x-hidden bg-surface px-6">
      {/* Logo / Brand */}
      <div className="mb-8 text-center">
        <div
          className={cn(
            "mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl",
            "bg-brand text-text-inverse text-2xl font-bold"
          )}
        >
          SC
        </div>
        <h1 className="text-2xl font-bold text-text-primary">Sales Copilot</h1>
        <p className="mt-1 text-sm text-text-secondary">
          ChemMasters field companion
        </p>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
        {error && (
          <div
            className="rounded-lg bg-error/10 px-4 py-3 text-sm text-error"
            role="alert"
          >
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="email"
            className="mb-1 block text-sm font-medium text-text-secondary"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={cn(
              "h-touch w-full rounded-lg border border-border bg-surface px-4",
              "text-text-primary placeholder:text-text-muted",
              "focus:border-brand focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand/20"
            )}
            placeholder="you@chemmasters.com"
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="mb-1 block text-sm font-medium text-text-secondary"
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={cn(
              "h-touch w-full rounded-lg border border-border bg-surface px-4",
              "text-text-primary placeholder:text-text-muted",
              "focus:border-brand focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand/20"
            )}
            placeholder="Enter password"
          />
        </div>

        {/* Stay logged in checkbox */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={stayLoggedIn}
            onChange={(e) => setStayLoggedIn(e.target.checked)}
            className="h-4 w-4 rounded border-border accent-brand"
          />
          <span className="text-sm text-text-secondary">Stay logged in</span>
        </label>

        <button
          type="submit"
          disabled={loading}
          className={cn(
            "h-touch w-full rounded-lg bg-brand font-semibold text-text-inverse",
            "transition-colors active:bg-brand-700",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}
